import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lead, Stage } from '@figureit/shared';
import { useLeads, useStages } from '../api/queries';
import type { ViewKey } from './TopBar';

/**
 * Cmd+K (Ctrl+K) global palette. Jump to any lead or switch view in two keystrokes.
 * Adapts the existing leads list as the search corpus — fast for typical pipelines,
 * and search runs against the same `searchText` index server-side.
 */
export function CommandPalette({
  open,
  onClose,
  onPickLead,
  onSwitchView,
  onNewLead,
}: {
  open: boolean;
  onClose: () => void;
  onPickLead: (id: string) => void;
  onSwitchView: (v: ViewKey) => void;
  onNewLead: () => void;
}) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Use the global leads cache (no search filter) so the user can fuzz-match.
  const { data } = useLeads();
  const { data: stagesData } = useStages();
  const stages = useMemo(() => stagesData?.items ?? [], [stagesData]);
  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const items = useMemo<PaletteItem[]>(() => {
    const base: PaletteItem[] = [
      { kind: 'view', view: 'overview', label: 'Go to Overview', hint: 'View' },
      { kind: 'view', view: 'kanban', label: 'Go to Pipeline', hint: 'View' },
      { kind: 'view', view: 'list', label: 'Go to List', hint: 'View' },
      { kind: 'view', view: 'reports', label: 'Go to Reports', hint: 'View' },
      { kind: 'view', view: 'audit', label: 'Go to Audit log', hint: 'View' },
      { kind: 'view', view: 'settings', label: 'Go to Settings', hint: 'View' },
      { kind: 'action', action: 'new-lead', label: 'New lead…', hint: 'Action' },
    ];
    const leadItems: PaletteItem[] = (data?.items ?? []).slice(0, 200).map((l) => ({
      kind: 'lead',
      lead: l,
      stage: stageMap[l.stageId],
      label: l.company,
      hint: stageMap[l.stageId]?.name ?? '',
    }));

    const all = [...base, ...leadItems];
    if (!q.trim()) return all.slice(0, 50);

    const needle = q.toLowerCase();
    return all
      .map((item) => ({ item, score: scoreItem(item, needle) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map((x) => x.item);
  }, [data, stageMap, q]);

  if (!open) return null;

  const pick = (it: PaletteItem) => {
    if (it.kind === 'view') onSwitchView(it.view);
    else if (it.kind === 'action' && it.action === 'new-lead') onNewLead();
    else if (it.kind === 'lead') onPickLead(it.lead.id);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-center pt-[max(0.5rem,env(safe-area-inset-top))] sm:pt-24 px-2 sm:px-0"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl w-full sm:w-[640px] sm:max-w-[90%] overflow-hidden shadow-2xl flex flex-col max-h-[calc(100dvh-1rem)] sm:max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="search"
          enterKeyHint="go"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(items.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (items[active]) pick(items[active]);
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
          placeholder="Jump to lead or run a command…"
          className="!border-0 !bg-transparent text-base px-4 py-3 !rounded-none flex-shrink-0"
        />
        <div className="border-t border-border flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {items.length === 0 && <div className="px-4 py-3 text-text3 text-sm">No matches.</div>}
          {items.map((it, i) => (
            <button
              key={keyFor(it, i)}
              className={
                'w-full flex items-center justify-between gap-3 px-4 py-3 sm:py-2.5 min-h-[44px] sm:min-h-0 text-left text-sm hover:bg-surface2 ' +
                (active === i ? 'bg-surface2' : '')
              }
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(it)}
            >
              <span className="flex items-center gap-2 truncate">
                {it.kind === 'lead' && it.stage && (
                  <span
                    className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0"
                    style={{ background: it.stage.color }}
                  />
                )}
                <span className="truncate">{it.label}</span>
                {it.kind === 'lead' && it.lead.contactName && (
                  <span className="text-text3 truncate">· {it.lead.contactName}</span>
                )}
              </span>
              <span className="text-[11px] text-text3 flex-shrink-0">{it.hint}</span>
            </button>
          ))}
        </div>
        <div className="hidden sm:flex border-t border-border px-4 py-2 text-[11px] text-text3 gap-3 flex-shrink-0">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}

type PaletteItem =
  | { kind: 'view'; view: ViewKey; label: string; hint: string }
  | { kind: 'action'; action: 'new-lead'; label: string; hint: string }
  | { kind: 'lead'; lead: Lead; stage?: Stage; label: string; hint: string };

const keyFor = (it: PaletteItem, i: number) => {
  if (it.kind === 'lead') return it.lead.id;
  if (it.kind === 'view') return `v:${it.view}`;
  if (it.kind === 'action') return `a:${it.action}`;
  return String(i);
};

const scoreItem = (it: PaletteItem, needle: string): number => {
  const haystacks: string[] = [it.label.toLowerCase()];
  if (it.kind === 'lead') {
    if (it.lead.contactName) haystacks.push(it.lead.contactName.toLowerCase());
    if (it.lead.email) haystacks.push(it.lead.email.toLowerCase());
  }
  let best = 0;
  for (const h of haystacks) {
    if (h === needle) best = Math.max(best, 100);
    else if (h.startsWith(needle)) best = Math.max(best, 80);
    else if (h.includes(needle)) best = Math.max(best, 50);
  }
  return best;
};
